import * as React from 'react';

import styled from '@emotion/styled';
import { Button } from '@lightspeed/flame/Button';
import { Box } from '@lightspeed/flame/Core';

import { AlertMessageDTO } from 'root/apps/components/AlertMessageDisplay/AlertMessageDisplay';
import {
  SendToNuorderModal,
  SendToNuorderProcessingModal,
  SendToNuorderSuccessModal,
} from 'root/apps/PurchaseOrder/NuorderOrdering/components/SendToNuorderModal';
import {
  LOG_SOURCE,
  NuorderOrderingActionTypes,
  useNuorderOrdering,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Context';
import {
  useCompleteNuorderOrder,
  useCreateNuorderOrder,
  useUpdateNuorderOrder,
  useUpdatePurchase,
} from 'root/apps/PurchaseOrder/NuorderOrdering/context/Queries';
import {
  ItemReadiness,
  NuorderOrderLineItemRequest,
  NuorderOrderRequest,
} from 'root/apps/PurchaseOrder/NuorderOrdering/types';
import {
  computeIsReadOnly,
  computeItemReadiness,
  computeNuorderAvailability,
  getDateFromNuorderOrder,
  getItemB2bCatalogUUID,
} from 'root/apps/PurchaseOrder/NuorderOrdering/utils/dataUtils';
import {
  computeAlertMessageFromNuorderErrorMessage,
  getAlertMessageByType,
  getErrorMessageFromNuorderError,
  getErrorStatusCodeFromNuorderError,
  SendToNuorderAlertMessageTypes,
  splitLineItemsPerAvailability,
} from 'root/apps/PurchaseOrder/NuorderOrdering/utils/sendToNuorderUtils';
import { logError } from 'root/datadog/datadogLogs';

const ButtonStyled = styled(Button)`
  white-space: nowrap;
`;

type ReadinessLineItems = NuorderOrderLineItemRequest & {
  itemReadiness: ItemReadiness;
};

type ModalState = 'closed' | 'confirm' | 'processing' | 'success';

export const SendToNuorderButton: React.FC = () => {
  const {
    state: {
      purchase,
      vendor,
      vendorShop,
      purchaseLines,
      items,
      nuorderProductAvailability,
      nuorderOrder,
      configs,
    },
    dispatch,
  } = useNuorderOrdering();

  const [modalState, setModalState] = React.useState<ModalState>('closed');

  const mutationCreateNuorderOrder = useCreateNuorderOrder(vendor?.b2bSellerUID || '');
  const mutationUpdateNuorderOrder = useUpdateNuorderOrder(
    vendor?.b2bSellerUID || '',
    nuorderOrder?.order_id || ''
  );
  const mutationCompleteNuorderOrder = useCompleteNuorderOrder(
    vendor?.b2bSellerUID || '',
    nuorderOrder?.order_id || ''
  );
  const mutationUpdatePurchase = useUpdatePurchase(purchase?.orderID || 0);

  /**
   * Computed values - Data
   * --------------------------------------------------------------------------
   */
  const customerPoNumber = React.useMemo(() => {
    return purchase?.orderID.toString() || '';
  }, [purchase]);

  const currencyCode = React.useMemo(() => {
    return vendor?.purchasingCurrency?.code || '';
  }, [vendor]);

  const shippingAddressID = React.useMemo(() => {
    return vendorShop?.b2bShippingAddressUID || '';
  }, [vendorShop]);

  const notes = React.useMemo(() => {
    return purchase?.Note?.note || '';
  }, [purchase]);

  const shipStart = React.useMemo(() => {
    // NuORDER expects a UNIX timestamp in seconds; pad with 1 minute so timezone math
    // doesn't bump the date to the previous day.
    return purchase?.arrivalDate ? Math.floor(purchase.arrivalDate.getTime() / 1000) + 60 : 0;
  }, [purchase]);

  /** Extract PurchaseLines data points to facilitate validation and inventory warehouse splitting */
  const readinessLineItems = React.useMemo<ReadinessLineItems[]>(() => {
    if (!vendor || !purchaseLines || !items || !nuorderProductAvailability) return [];
    if (!vendor.b2bSellerUID) return [];
    if (purchaseLines.length === 0) return [];
    return purchaseLines
      .filter((purchaseLine) => items[purchaseLine.itemID])
      .map((purchaseLine) => {
        const item = items[purchaseLine.itemID];
        const b2bCatalogUUID = getItemB2bCatalogUUID(item, vendor.vendorID);
        const itemReadiness = computeItemReadiness(
          item.upc,
          b2bCatalogUUID,
          purchaseLine.quantity,
          computeNuorderAvailability(
            item.itemID,
            vendor.vendorID,
            items,
            nuorderProductAvailability
          ),
          nuorderProductAvailability
        );
        return {
          product_id: b2bCatalogUUID,
          quantity: purchaseLine.quantity,
          itemReadiness,
        };
      });
  }, [items, nuorderProductAvailability, purchaseLines, vendor]);

  const lineItems: NuorderOrderLineItemRequest[] = React.useMemo(() => {
    if (!nuorderProductAvailability) return [];
    if (!readinessLineItems || readinessLineItems.length === 0) return [];
    return splitLineItemsPerAvailability(readinessLineItems, nuorderProductAvailability);
  }, [readinessLineItems, nuorderProductAvailability]);

  /**
   * Computed values - state
   * --------------------------------------------------------------------------
   */
  const isDraft = React.useMemo(() => {
    return nuorderOrder?.status === 'draft';
  }, [nuorderOrder]);

  const isReadOnly = React.useMemo(() => {
    return computeIsReadOnly(purchase, nuorderOrder);
  }, [nuorderOrder, purchase]);

  const isLoading = React.useMemo(() => {
    return (
      mutationCreateNuorderOrder.isLoading ||
      mutationUpdateNuorderOrder.isLoading ||
      mutationCompleteNuorderOrder.isLoading ||
      mutationUpdatePurchase.isLoading
    );
  }, [
    mutationCreateNuorderOrder.isLoading,
    mutationUpdateNuorderOrder.isLoading,
    mutationCompleteNuorderOrder.isLoading,
    mutationUpdatePurchase.isLoading,
  ]);

  const isEnabled = React.useMemo(() => {
    return readinessLineItems.length !== 0;
  }, [readinessLineItems]);

  /**
   * Computed values - UI
   * --------------------------------------------------------------------------
   */
  const viewOnNuorderLink = React.useMemo(() => {
    if (nuorderOrder?.order_id && vendor?.b2bSellerUID && configs?.b2bWebServiceBaseURI) {
      return `${configs.b2bWebServiceBaseURI}/brand/${vendor.b2bSellerUID}/order/${nuorderOrder.order_id}`;
    }
    return '';
  }, [nuorderOrder, vendor, configs]);

  const vendorLink = React.useMemo(() => {
    return `${window.location.protocol}//${window.location.host}/?name=vendor.views.vendor&form_name=view&id=${vendor?.vendorID}&tab=details`;
  }, [vendor]);

  const vendorLinkingLink = React.useMemo(() => {
    return `${window.location.protocol}//${window.location.host}/inventory/vendors/b2b`;
  }, []);

  /**
   * Methods
   * --------------------------------------------------------------------------
   */
  const resetMutations = React.useCallback(() => {
    mutationCreateNuorderOrder.reset();
    mutationUpdateNuorderOrder.reset();
    mutationCompleteNuorderOrder.reset();
    mutationUpdatePurchase.reset();
  }, [
    mutationCreateNuorderOrder,
    mutationUpdateNuorderOrder,
    mutationCompleteNuorderOrder,
    mutationUpdatePurchase,
  ]);

  const setAlertMessage = React.useCallback(
    (message: AlertMessageDTO | null) => {
      dispatch({
        type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
        payload: message,
      });
    },
    [dispatch]
  );

  const validateOrder = React.useCallback(() => {
    if (
      readinessLineItems.some(({ itemReadiness }) => {
        if (itemReadiness === ItemReadiness.NO_UPC) return true;
        if (itemReadiness === ItemReadiness.NO_UUID) return true;
        if (itemReadiness === ItemReadiness.NOT_ON_NUORDER) return true;
        return false;
      })
    ) {
      setAlertMessage(getAlertMessageByType(SendToNuorderAlertMessageTypes.NOT_READY));
      return false;
    }
    if (!currencyCode) {
      setAlertMessage(
        getAlertMessageByType(SendToNuorderAlertMessageTypes.MISSING_CURRENCY_CODE, {
          href: vendorLink,
        })
      );
      return false;
    }
    if (!shippingAddressID) {
      setAlertMessage(
        getAlertMessageByType(SendToNuorderAlertMessageTypes.MISSING_SHIPPING_ADDRESS, {
          href: vendorLinkingLink,
        })
      );
      return false;
    }
    return true;
  }, [
    currencyCode,
    readinessLineItems,
    setAlertMessage,
    shippingAddressID,
    vendorLink,
    vendorLinkingLink,
  ]);

  const createNuorderOrder = React.useCallback(() => {
    if (isLoading) return;
    resetMutations();
    if (!validateOrder()) return;

    const nuorderOrderRequest: NuorderOrderRequest = {
      currency_code: currencyCode,
      line_items: lineItems,
      shipping_address_id: shippingAddressID,
      draft: true,
      customer_po_number: customerPoNumber,
    };
    if (notes) nuorderOrderRequest.notes = notes;
    if (shipStart) nuorderOrderRequest.ship_start = shipStart;

    mutationCreateNuorderOrder.mutate(nuorderOrderRequest);
  }, [
    isLoading,
    resetMutations,
    validateOrder,
    currencyCode,
    lineItems,
    shippingAddressID,
    customerPoNumber,
    notes,
    shipStart,
    mutationCreateNuorderOrder,
  ]);

  const updateNuorderOrder = React.useCallback(() => {
    if (isLoading) return;
    if (!nuorderOrder || !isDraft) return;
    resetMutations();
    if (!validateOrder()) return;

    const nuorderOrderRequest: NuorderOrderRequest = {
      currency_code: currencyCode,
      line_items: lineItems,
      shipping_address_id: shippingAddressID,
      customer_po_number: customerPoNumber,
    };
    if (notes) nuorderOrderRequest.notes = notes;
    if (shipStart) nuorderOrderRequest.ship_start = shipStart;

    mutationUpdateNuorderOrder.mutate(nuorderOrderRequest);
  }, [
    currencyCode,
    isDraft,
    isLoading,
    lineItems,
    mutationUpdateNuorderOrder,
    nuorderOrder,
    customerPoNumber,
    resetMutations,
    shippingAddressID,
    validateOrder,
    notes,
    shipStart,
  ]);

  const completeNuorderOrder = React.useCallback(() => {
    if (isLoading) return;
    if (!nuorderOrder || !isDraft) return;
    resetMutations();

    mutationCompleteNuorderOrder.mutate();
  }, [isDraft, isLoading, mutationCompleteNuorderOrder, nuorderOrder, resetMutations]);

  /**
   * Effects - Data Sequencing
   * --------------------------------------------------------------------------
   */
  /**
   * Try completing the order after creation and update purchase
   */
  React.useEffect(() => {
    if (!mutationCreateNuorderOrder.data) return;
    if (mutationCreateNuorderOrder.data !== nuorderOrder) return;
    if (mutationUpdatePurchase.isLoading) return;
    resetMutations();
    completeNuorderOrder();
    const orderedDate =
      getDateFromNuorderOrder(mutationCreateNuorderOrder.data.created_on) ?? new Date();
    mutationUpdatePurchase.mutate({
      b2bOrderUID: mutationCreateNuorderOrder.data.order_id,
      b2bOrderNumber: mutationCreateNuorderOrder.data.order_number,
      orderedDate: orderedDate.toISOString(),
    });
    // optimistically update purchase to avoid completeNuorderOrder finishing before purchase.b2bOrderUID is set
    dispatch({
      type: NuorderOrderingActionTypes.UPDATE_PURCHASE,
      payload: {
        b2bOrderUID: mutationCreateNuorderOrder.data.order_id,
        b2bOrderNumber: mutationCreateNuorderOrder.data.order_number,
        orderedDate,
      },
    });
  }, [
    completeNuorderOrder,
    dispatch,
    mutationCreateNuorderOrder.data,
    mutationUpdatePurchase,
    nuorderOrder,
    resetMutations,
  ]);

  /** Update purchase after finalizing draft order completion */
  React.useEffect(() => {
    if (!mutationCompleteNuorderOrder.data) return;
    if (mutationCompleteNuorderOrder.data !== nuorderOrder) return;
    if (mutationUpdatePurchase.isLoading) return;
    const orderedDate =
      getDateFromNuorderOrder(mutationCompleteNuorderOrder.data.created_on) ?? new Date();
    resetMutations();
    mutationUpdatePurchase.mutate({
      b2bOrderUID: mutationCompleteNuorderOrder.data.order_id,
      b2bOrderNumber: mutationCompleteNuorderOrder.data.order_number,
      orderedDate: orderedDate.toISOString(),
    });
  }, [mutationCompleteNuorderOrder.data, mutationUpdatePurchase, nuorderOrder, resetMutations]);

  /** Try completing the order after update */
  React.useEffect(() => {
    if (mutationUpdateNuorderOrder.data && mutationUpdateNuorderOrder.data === nuorderOrder) {
      resetMutations();
      completeNuorderOrder();
    }
  }, [completeNuorderOrder, mutationUpdateNuorderOrder, nuorderOrder, resetMutations]);

  /**
   * Effects - Success/Error Messages
   * --------------------------------------------------------------------------
   */

  React.useEffect(() => {
    if (isLoading && modalState !== 'success') {
      setModalState('processing');
    }
  }, [isLoading, modalState]);

  // eslint-disable-next-line consistent-return
  React.useEffect(() => {
    if (modalState === 'success') {
      const timeout = setTimeout(() => {
        setModalState('closed');
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [modalState]);

  React.useEffect(() => {
    if (!mutationCompleteNuorderOrder.data) return;
    if (mutationCompleteNuorderOrder.data !== nuorderOrder) return;
    setModalState('success');
    setAlertMessage(
      getAlertMessageByType(SendToNuorderAlertMessageTypes.SUCCESS_COMPLETE_ORDER, {
        href: viewOnNuorderLink,
      })
    );
  }, [
    dispatch,
    mutationCompleteNuorderOrder.data,
    nuorderOrder,
    setAlertMessage,
    viewOnNuorderLink,
  ]);

  React.useEffect(() => {
    if (
      !mutationCreateNuorderOrder.error &&
      !mutationUpdateNuorderOrder.error &&
      !mutationCompleteNuorderOrder.error
    )
      return;
    const error =
      mutationCreateNuorderOrder.error ??
      mutationUpdateNuorderOrder.error ??
      mutationCompleteNuorderOrder.error;
    const errorMessage = getErrorMessageFromNuorderError(error);
    const alertMessage = computeAlertMessageFromNuorderErrorMessage({
      errorMessage,
      errorStatusCode: getErrorStatusCodeFromNuorderError(error),
      isCompletionError: error === mutationCompleteNuorderOrder.error,
      vendorLink,
      vendorLinkingLink,
      viewOnNuorderLink,
    });
    dispatch({
      type: NuorderOrderingActionTypes.SET_ALERT_MESSAGE,
      payload: alertMessage,
    });

    logError(error, {
      source: LOG_SOURCE,
      file: 'SendToNuorderButton.tsx',
    });
    setModalState('closed');
  }, [
    dispatch,
    mutationCompleteNuorderOrder.error,
    mutationCreateNuorderOrder.error,
    mutationUpdateNuorderOrder.error,
    vendorLink,
    vendorLinkingLink,
    viewOnNuorderLink,
  ]);

  /**
   * UI
   * --------------------------------------------------------------------------
   */
  const handleSendToNuorder = () => {
    setModalState('confirm');
  };
  const handleCancel = () => {
    setModalState('closed');
  };
  const handleConfirm = () => {
    setModalState('closed');
    setAlertMessage(null);
    if (isDraft) {
      updateNuorderOrder();
      return;
    }
    createNuorderOrder();
  };

  return (
    <Box mb={6}>
      {!isReadOnly ? (
        <ButtonStyled
          variant="secondary"
          fill
          onClick={handleSendToNuorder}
          disabled={!isEnabled}
          loading={isLoading}
          data-test="send-to-nuorder-button"
        >
          {isDraft ? 'Finalize draft order' : 'Send to NuORDER'}
        </ButtonStyled>
      ) : null}
      <SendToNuorderModal
        id={purchase?.orderID || 0}
        isOpen={modalState === 'confirm'}
        onClose={handleCancel}
        onSend={handleConfirm}
      />
      <SendToNuorderProcessingModal isOpen={modalState === 'processing'} onClose={handleCancel} />
      <SendToNuorderSuccessModal
        id={purchase?.orderID || 0}
        isOpen={modalState === 'success'}
        onClose={handleCancel}
      />
    </Box>
  );
};
